import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AccountsPayableService } from './accounts-payable.service';
import { ToasterService } from '../utils/toaster.service';

export const AP_BULK_INVOICE_MAX_SIZE_MB = 50;
export const AP_BULK_INVOICE_ALLOWED_EXTENSIONS = [
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
] as const;
export const AP_BULK_INVOICE_ALLOWED_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
] as const;

/** @deprecated Prefer AP_BULK_INVOICE_ALLOWED_TYPES */
export const AP_BULK_INVOICE_ALLOWED_TYPE = 'application/pdf';

export interface ApBulkInvoiceUploadResult {
  succeeded: number;
  failed: number;
}

export type ProcessBatchDocumentType = 'AccountsPayable' | 'expense';

export interface ApBulkInvoiceUploadOptions {
  documentType?: ProcessBatchDocumentType;
}

@Injectable({ providedIn: 'root' })
export class ApBulkInvoiceUploadService {
  private readonly maxFileSizeBytes = AP_BULK_INVOICE_MAX_SIZE_MB * 1024 * 1024;

  constructor(
    private apService: AccountsPayableService,
    private toaster: ToasterService
  ) {}

  uploadAndCreateDrafts(
    files: File[],
    options?: ApBulkInvoiceUploadOptions
  ): Observable<ApBulkInvoiceUploadResult> {
    const documentType: ProcessBatchDocumentType =
      options?.documentType ?? 'AccountsPayable';
    const validFiles = this.filterValidFiles(files);
    if (!validFiles.length) {
      return of({ succeeded: 0, failed: files.length });
    }

    // Send every file in a single request — BE binds `documents` as a
    // collection, so one process-batch call handles the whole selection.
    return this.apService.processFilesBatch(validFiles, documentType).pipe(
      map(() => ({ succeeded: validFiles.length, failed: 0 })),
      catchError((error) => {
        this.toaster.showError(
          error?.error?.message ||
            error?.message ||
            'Could not create draft requests from the selected files.'
        );
        return of({ succeeded: 0, failed: validFiles.length });
      })
    );
  }

  private filterValidFiles(files: File[]): File[] {
    const accepted: File[] = [];
    const seen = new Set<string>();

    for (const file of files) {
      const key = `${file.name}_${file.size}_${file.lastModified}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);

      const name = file.name.toLowerCase();
      const hasAllowedExt = AP_BULK_INVOICE_ALLOWED_EXTENSIONS.some((ext) =>
        name.endsWith(ext)
      );
      const hasAllowedType =
        !!file.type &&
        (AP_BULK_INVOICE_ALLOWED_TYPES as readonly string[]).includes(file.type);

      if (!hasAllowedExt && !hasAllowedType) {
        this.toaster.showError(
          `File "${file.name}" is not supported. Allowed: PDF, PNG, JPG or JPEG.`
        );
        continue;
      }

      if (file.size > this.maxFileSizeBytes) {
        this.toaster.showError(
          `File "${file.name}" exceeds the maximum size of ${AP_BULK_INVOICE_MAX_SIZE_MB}MB.`
        );
        continue;
      }

      accepted.push(file);
    }

    return accepted;
  }
}
